/**
 * useUrlSearchParamSync — one-way mirror from React state to a URL
 * search-param, built on top of react-router's `useSearchParams`.
 *
 * Each render writes `value` into the URL via the router's navigate
 * (with `{ replace: true }` so we don't pollute the back-stack), and
 * clears the param when the value is null / empty.
 *
 * The hook short-circuits when the URL already matches — without this
 * guard, every render would call `setSearchParams`, which always
 * triggers a navigation and would loop the effect via the
 * `searchParams` dep.
 *
 * Replaces the hand-rolled `useUrlSearchParam` hook that wrote to
 * `window.history.replaceState` directly. That helper predated the
 * routing pack (2026-05-13); now that BrowserRouter wraps the app,
 * URL-state-sync should flow through react-router so the router's
 * internal location stays in step with `window.location`.
 *
 * Callers that genuinely want a new history entry (eg. a deep-link
 * tab change driven by a button click) should still call
 * `navigate(url)` or `window.history.pushState` explicitly — this hook
 * is for the state-mirror case only.
 */
import { useEffect } from 'react';
import { useInRouterContext, useSearchParams } from 'react-router-dom';

/**
 * Internal — runs only inside a Router context, where useSearchParams
 * is callable. Split out so the rules-of-hooks invariant ("same
 * hooks, same order, every render") holds for the outer dispatcher
 * which picks one of two children based on Router presence.
 */
function useUrlSearchParamSyncRouter(key: string, value: string | null, enabled: boolean): void {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!enabled) return;
    const current = searchParams.get(key);
    const empty = value == null || value === '';
    // No-op when the URL already matches. This guard is required, not
    // just an optimisation: setSearchParams always navigates, which
    // produces a new `searchParams` reference and would re-fire this
    // effect on every render without it.
    if (empty && current === null) return;
    if (!empty && current === value) return;

    const next = new URLSearchParams(searchParams);
    if (empty) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }, [key, value, enabled, searchParams, setSearchParams]);
}

/**
 * Internal — no-Router fallback. EDX-5 added this branch so callers
 * (DocumentLibrary specifically) that may be mounted in tests without
 * a wrapping MemoryRouter still type-check and run. Uses
 * `window.history.replaceState` directly. Always lives in the
 * `useEffect` so we never call it during render.
 */
function useUrlSearchParamSyncFallback(key: string, value: string | null, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const empty = value == null || value === '';
      const current = url.searchParams.get(key);
      if (empty && current === null) return;
      if (!empty && current === value) return;
      if (empty) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
      window.history.replaceState({}, '', url.toString());
    } catch {
      // best-effort — URL-sync is purely a deep-link nicety.
    }
  }, [key, value, enabled]);
}

/**
 * `enabled` lets a route-owned mirror wait for navigation to settle. A
 * temporarily mounted page must not write using the departing route.
 *
 * Public — picks the right sub-hook by Router presence. Both sub-hooks
 * are wrapped in a one-shot if/else dispatcher so the rules of hooks
 * are preserved: a given component instance always travels through
 * the same branch (Router presence is determined by the parent tree,
 * which doesn't change across renders).
 */
export function useUrlSearchParamSync(key: string, value: string | null, enabled = true): void {
  const inRouterContext = useInRouterContext();
  if (inRouterContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useUrlSearchParamSyncRouter(key, value, enabled);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useUrlSearchParamSyncFallback(key, value, enabled);
  }
}
